import React from 'react';
import { Box, Grid, Paper, useMediaQuery, useTheme, Typography } from '@mui/material';
import { ConversationList } from '../components/messaging/ConversationList';
import { ConversationView } from '../components/messaging/ConversationView';
import { useMessaging } from '../contexts/MessagingContext';

/**
 * Messages Page
 *
 * Main messaging interface with:
 * - Responsive layout (mobile/desktop)
 * - Conversation list sidebar
 * - Active conversation view
 * - Empty state when no conversation selected
 *
 * Mobile behavior:
 * - Shows conversation list OR active conversation (not both)
 * - Back button in header navigates to conversation list
 *
 * Desktop behavior:
 * - Shows conversation list and active conversation side-by-side
 */
const Messages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { activeConversationId, setActiveConversation } = useMessaging();

  // Handle back navigation on mobile
  const handleMobileBack = (): void => {
    setActiveConversation(null);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'hidden', p: 2 }}>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Conversation List */}
        {(!isMobile || !activeConversationId) && (
          <Grid item xs={12} md={4} sx={{ height: '100%' }}>
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <ConversationList />
            </Paper>
          </Grid>
        )}

        {/* Conversation View */}
        {(!isMobile || activeConversationId) && (
          <Grid item xs={12} md={8} sx={{ height: '100%' }}>
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {activeConversationId ? (
                <ConversationView onBack={isMobile ? handleMobileBack : undefined} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: 2,
                    p: 4,
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    Select a conversation to start messaging
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Choose a conversation from the list or create a new one to begin chatting.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Messages;
